import React, { useRef, useState } from 'react';
import { Tag } from 'lucide-react';
import { cn } from '../../lib/utils';
import CustomValuesPicker from './CustomValuesPicker';
import { applyInputInsert } from '../../utils/insertMergeField';

const MergeFieldInput = ({
  value,
  onChange,
  onValueChange,
  token,
  className,
  inputClassName,
  disabled,
  multiline = false,
  ...inputProps
}) => {
  const inputRef = useRef(null);
  const tagRef = useRef(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const { className: nativeClassName, ...restInputProps } = inputProps;

  const handleChange = (e) => {
    onChange?.(e);
    onValueChange?.(e.target.value);
  };

  const handleInsert = (mergeToken) => {
    const el = inputRef.current;
    const apply = (nextValue) => {
      if (onChange) {
        onChange({ target: { value: nextValue } });
      }
      onValueChange?.(nextValue);
    };

    applyInputInsert(el, mergeToken, apply);
    setPickerOpen(false);
  };

  const tagTestId = restInputProps['data-testid']
    ? `${restInputProps['data-testid']}-merge-tag`
    : 'merge-field-tag';

  const fieldClassName = cn(
    'min-w-0 flex-1 border-0 bg-transparent px-3 text-slate-900 shadow-none outline-none',
    'placeholder:text-slate-400 disabled:cursor-not-allowed disabled:opacity-50',
    'focus-visible:ring-0 focus-visible:ring-offset-0',
    multiline ? 'min-h-[60px] resize-y py-2 text-sm' : 'h-9 py-1 text-sm',
    inputClassName,
    nativeClassName,
  );

  return (
    <div className={cn('w-full', className)}>
      <div
        className={cn(
          'flex w-full overflow-hidden rounded-md border border-slate-300 bg-white shadow-sm transition-colors',
          'focus-within:border-teal-500 focus-within:ring-1 focus-within:ring-teal-500',
          multiline ? 'items-start' : 'items-stretch',
          disabled && 'cursor-not-allowed opacity-50',
        )}
      >
        {multiline ? (
          <textarea
            ref={inputRef}
            value={value}
            onChange={handleChange}
            disabled={disabled}
            className={fieldClassName}
            {...restInputProps}
          />
        ) : (
          <input
            ref={inputRef}
            value={value}
            onChange={handleChange}
            disabled={disabled}
            className={fieldClassName}
            {...restInputProps}
          />
        )}
        <button
          ref={tagRef}
          type="button"
          disabled={disabled}
          data-testid={tagTestId}
          onClick={() => setPickerOpen((prev) => !prev)}
          className={cn(
            'flex shrink-0 items-center justify-center border-l border-slate-300 bg-slate-50 px-2.5',
            'text-slate-500 transition-colors hover:bg-slate-100 hover:text-blue-600',
            'disabled:pointer-events-none disabled:opacity-50',
            multiline ? 'self-stretch py-2' : 'w-9',
          )}
          aria-label="Insert custom value"
          title="Custom Values"
        >
          <Tag size={15} strokeWidth={2} />
        </button>
      </div>
      <CustomValuesPicker
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onInsert={handleInsert}
        anchorRef={tagRef}
        token={token}
      />
    </div>
  );
};

export default MergeFieldInput;
